import { ArrowDownCircle, ArrowLeft, ArrowUpCircle, Check, ChevronDown, Clock, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { openSupportChatwoot } from '../../features/support/chatwoot';
import { fetchWalletData, type WalletOrder } from '../../features/wallet/api';
import { UnpaidDepositCard } from '../../features/wallet/UnpaidDepositCard';
import { DEPOSIT_ORDERS_QUERY_KEY } from '../../features/wallet/useDepositOrders';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { localClock, localDateTime } from '../../shared/lib/datetime';
import { fmtMoney } from '../../shared/lib/format';
import { DetailRow } from '../../shared/ui/DetailRow';
import { orderStatusBucket, orderStatusLabelKey } from '../../shared/wallet/orderStatus';
import { filterOrders, findOrderByRouteId, groupOrdersByDay, normalizeOrders, orderRouteId, orderStatusFilterLabelKey, orderSupportRoute, orderTimeline, withdrawFeePercent, type OrderKindFilter, type OrderStatusFilter, type TimelineRow, type UiOrder } from '../../shared/wallet/orderView';
import { isInternalPaymentChannel } from '../../shared/wallet/paymentChannels';
import { refreshWallet } from '../../shared/wallet/refreshWallet';
import { isUnpaidDeposit, type UnpaidCardState } from '../../shared/wallet/unpaidDeposits';
import '../../features/wallet/unpaidDeposits.css';
import '../../features/wallet/transactionHistory.css';

export function TransactionHistoryPage() {
  const t = useI18nStore((s) => s.t);
  const language = useI18nStore((s) => s.language);
  const token = useAuthStore((s) => s.token) || '';
  const guestMode = useAuthStore((s) => s.guestMode);
  const user = useAuthStore((s) => s.user);
  const country = useCountryStore((s) => s.country);
  const activeOrderId = useAppStore((s) => s.activeOrderId);
  const setActiveOrderId = useAppStore((s) => s.setActiveOrderId);
  const goBack = useAppStore((s) => s.goBack);
  const showToast = useAppStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const walletQuery = useQuery({
    queryKey: ['wallet', token, country.code, country.fiatCurrency, 'full-order-history'],
    queryFn: () => fetchWalletData(token, { fullOrderHistory: true }),
    enabled: !!token && !guestMode,
  });
  const orders = useMemo(() => normalizeOrders(walletQuery.data?.deposits || [], walletQuery.data?.withdrawals || []), [walletQuery.data]);
  const nowSeconds = Math.floor(Date.now() / 1000);
  const support = (order: UiOrder) => openSupportChatwoot({ user, language, route: orderSupportRoute(order) }).catch(() => showToast(t('supportChatError'), 'warn'));

  const onCredited = useCallback(() => {
    void refreshWallet(queryClient, token);
    // The Profile "needs action" badge reads the shared deposit-orders cache
    // (useDepositOrders), a separate query from this page's own wallet fetch
    // — refreshWallet alone never touched it, so the badge kept the stale
    // count after a proof got the order credited here.
    void queryClient.invalidateQueries({ queryKey: [DEPOSIT_ORDERS_QUERY_KEY, token] });
  }, [queryClient, token]);

  if (activeOrderId) {
    const order = findOrderByRouteId(orders, activeOrderId);
    const raw = order ? (walletQuery.data?.deposits || []).find((d) => String(d.publicId || '') === order.publicId) : undefined;
    return (
      <OrderDetailView order={order} raw={raw} loading={walletQuery.isLoading}
        error={walletQuery.isError} onRetry={() => walletQuery.refetch()}
        onBack={() => goBack('me.history')} onList={() => setActiveOrderId(null)} onSupport={support}
        onCredited={onCredited} />
    );
  }
  return (
    <HistoryListView orders={orders} nowSeconds={nowSeconds}
      loading={walletQuery.isLoading} error={walletQuery.isError} onRetry={() => walletQuery.refetch()}
      onBack={() => goBack('me')}
      onOpen={(order) => setActiveOrderId(orderRouteId(order), { push: true })} />
  );
}

function HistoryListView({ orders, nowSeconds, loading, error, onRetry, onBack, onOpen }: {
  orders: UiOrder[];
  nowSeconds: number;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onBack: () => void;
  onOpen: (order: UiOrder) => void;
}) {
  const t = useI18nStore((s) => s.t);
  const [kind, setKind] = useState<OrderKindFilter>('deposit');
  const [status, setStatus] = useState<OrderStatusFilter>('all');
  const depositCount = orders.filter((o) => o.kind === 'deposit').length;
  const withdrawCount = orders.length - depositCount;
  const rows = useMemo(() => filterOrders(orders, kind, status), [orders, kind, status]);
  const groups = useMemo(() => groupOrdersByDay(rows, nowSeconds, t), [rows, nowSeconds, t]);
  return (
    <section className="tab-page history-page" data-history-page-react="true">
      <header className="subroute-header">
        <button className="icon-button" type="button" onClick={onBack} aria-label={t('back')}><ArrowLeft size={19} aria-hidden="true" /></button>
        <div><h1>{t('meTransactionRecords')}</h1></div>
      </header>
      <div className="orders-kind-switch">
        <button type="button" className={kind === 'deposit' ? 'active deposit' : ''} onClick={() => setKind('deposit')}><ArrowDownCircle size={14} aria-hidden="true" /><span>{t('pedidosTabDeposits')}</span><b>{depositCount}</b></button>
        <button type="button" className={kind === 'withdraw' ? 'active withdraw' : ''} onClick={() => setKind('withdraw')}><ArrowUpCircle size={14} aria-hidden="true" /><span>{t('pedidosTabWithdraws')}</span><b>{withdrawCount}</b></button>
      </div>
      <div className="orders-status-row">
        {(['all', 'pending', 'completed', 'failed'] as OrderStatusFilter[]).map((s) => (
          <button key={s} type="button" className={status === s ? 'active' : ''} onClick={() => setStatus(s)}>{t(orderStatusFilterLabelKey(s))}</button>
        ))}
      </div>
      {loading ? <p className="history-state">{t('loading')}</p> : null}
      {error ? <p className="history-state" role="alert">{t('walletRefreshFailed')} <button type="button" className="unpaid-link" onClick={onRetry}>{t('retry')}</button></p> : null}
      {!loading && !error && rows.length === 0 ? (
        <p className="history-state">{orders.some((o) => o.kind === kind) ? t('pedidosEmptyFiltered') : t(kind === 'deposit' ? 'historyEmptyDeposits' : 'historyEmptyWithdrawals')}</p>
      ) : null}
      {groups.map((group) => (
        <section className="history-day" key={group.key}>
          <h3>{group.label}</h3>
          {group.orders.map((order) => <HistoryRow key={`${order.kind}:${order.id}`} order={order} onClick={() => onOpen(order)} />)}
        </section>
      ))}
    </section>
  );
}

function HistoryRow({ order, onClick }: { order: UiOrder; onClick: () => void }) {
  const t = useI18nStore((s) => s.t);
  const bucket = orderStatusBucket(order.status, order.kind);
  const deposit = order.kind === 'deposit';
  const Icon = deposit ? ArrowDownCircle : ArrowUpCircle;
  const channel = isInternalPaymentChannel({ channelId: order.channelId, label: order.channelLabel, network: order.network }) ? '' : (order.channelLabel || order.network || '');
  return (
    <button type="button" className={`history-row ${order.kind} is-${bucket}`} data-history-order={order.publicId} onClick={onClick}>
      <Icon className="history-row-icon" size={20} aria-hidden="true" />
      <span className="history-row-main">
        <span className="history-row-title">{t(deposit ? 'transactionKindDeposit' : 'transactionKindWithdrawal')}{channel ? ` · ${channel}` : ''}</span>
        <span className="history-row-meta">{localClock(order.createdAt)}</span>
        <span className="history-row-id">{order.publicId}</span>
        {!deposit ? <span className="history-row-net">{t('historyWithdrawNet').replace('%net%', fmtMoney(order.payoutAmount)).replace('%fee%', fmtMoney(order.feeAmount))}</span> : null}
      </span>
      <span className="history-row-side">
        <span className="history-row-amount">{deposit ? '+' : '−'}{fmtMoney(order.requestedAmount)}</span>
        <b className="history-status">{t(orderStatusLabelKey(order.status, order.kind))}</b>
      </span>
    </button>
  );
}

function OrderDetailView({ order, raw, loading, error, onRetry, onBack, onList, onSupport, onCredited }: { order: UiOrder | null; raw?: WalletOrder; loading: boolean; error: boolean; onRetry: () => void; onBack: () => void; onList: () => void; onSupport: (o: UiOrder) => void; onCredited: () => void }) {
  const t = useI18nStore((s) => s.t);
  const [proofState, setProofState] = useState<UnpaidCardState | null>(null);
  const header = (
    <header className="subroute-header">
      <button className="icon-button" type="button" onClick={onBack} aria-label={t('back')}><ArrowLeft size={19} aria-hidden="true" /></button>
      <div><h1>{t('pedidosDetailSheetTitle')}</h1></div>
    </header>
  );
  if (!order) {
    // A fetch failure and a genuinely missing order look identical once the
    // list is empty (`order` is null either way) — but they are not the same
    // problem: one is "try again", the other is "this order does not exist".
    // Telling them apart here means a flaky connection never reads as a
    // vanished order.
    if (error) {
      return (
        <section className="tab-page history-page" data-history-detail-react="error">
          {header}
          <p className="history-state" role="alert">{t('walletRefreshFailed')} <button type="button" className="unpaid-link" onClick={onRetry}>{t('retry')}</button></p>
        </section>
      );
    }
    return (
      <section className="tab-page history-page" data-history-detail-react="missing">
        {header}
        <p className="history-state">{loading ? t('loading') : t('historyOrderNotFound')}</p>
        {!loading ? <button type="button" className="primary-button history-back" onClick={onList}>{t('historyBackToList')}</button> : null}
      </section>
    );
  }
  const bucket = orderStatusBucket(order.status, order.kind);
  const deposit = order.kind === 'deposit';
  const providerId = order.providerOrderId || order.providerRef || '';
  const channel = isInternalPaymentChannel({ channelId: order.channelId, label: order.channelLabel, network: order.network }) ? '' : (order.channelLabel || order.channelId || order.network || '');
  const recoverable = deposit && isUnpaidDeposit(order, Date.now() / 1000) && !!raw;
  const timeline = orderTimeline(order, bucket, t);
  const rows: TimelineRow[] = timeline.length === 2
    ? [...timeline, { label: deposit ? t('pedidosTimelineCredited') : t('pedidosTimelineCompleted'), time: '', state: 'muted' }]
    : timeline;
  const steps = rows.map((row, i) => {
    let note = '';
    if (row.state === 'current') {
      note = t('pedidosDetailHelpPending');
    } else if (i === rows.length - 1) {
      if (row.state === 'failed') note = t('pedidosDetailHelpFailed');
      else if (bucket === 'completed') note = t('pedidosDetailHelpCompleted');
    }
    return { ...row, note };
  });
  return (
    <section className="tab-page history-page history-detail" data-history-detail-react={order.publicId}>
      {header}
      <section className={`order-detail-hero ${bucket}`}>
        <div>
          <span>{[t(deposit ? 'transactionKindDeposit' : 'transactionKindWithdrawal'), channel].filter(Boolean).join(' · ')}</span>
          <strong>{fmtMoney(order.amount)}</strong>
        </div>
        <b>
          {recoverable && proofState === 'in_review' ? t('historyStatusProofReview')
            : recoverable && proofState === 'resubmit' ? t('unpaidStepRejected')
            : bucket === 'pending' && deposit ? t('historyStatusWaitingPayment')
            : t(orderStatusLabelKey(order.status, order.kind))}
        </b>
      </section>
      {recoverable && raw ? (
        <UnpaidDepositCard order={raw} highlighted={false} compact onCredited={onCredited} onSupport={() => onSupport(order)} onStateChange={setProofState} />
      ) : (
        <section className="order-detail-card">
          <ol className="history-steps">
            {steps.map((row, i) => {
              const icon = row.state === 'done' ? <Check size={14} aria-hidden="true" />
                : row.state === 'current' ? <Clock size={14} aria-hidden="true" />
                : row.state === 'failed' ? <X size={14} aria-hidden="true" />
                : null;
              return (
                <li key={`${row.label}-${i}`} className={`history-step is-${row.state}`} data-step-state={row.state}>
                  <i className="proof-dot" aria-hidden="true">{icon}</i>
                  <div className="history-step-body">
                    <div className="history-step-head"><strong>{row.label}</strong>{row.time ? <time>{row.time}</time> : null}</div>
                    {row.note ? <p>{row.note}</p> : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      )}
      <section className="order-detail-card history-details">
        <DetailRow label={t('pedidosDetailIdMerchant')} value={order.publicId || order.id || '-'} copyable />
        <details className="history-more">
          <summary>{t('historyMoreDetails')}<ChevronDown size={16} aria-hidden="true" /></summary>
          {providerId ? <DetailRow label={t('pedidosDetailIdProvider')} value={providerId} copyable /> : null}
          {channel ? <DetailRow label={t('pedidosDetailChannel')} value={channel} /> : null}
          <DetailRow label={t('pedidosDetailCreated')} value={localDateTime(order.createdAt)} />
          {order.paidAt ? <DetailRow label={t('pedidosDetailPaidAt')} value={localDateTime(order.paidAt)} /> : null}
          {bucket === 'pending' && order.expiresAt ? <DetailRow label={t('pedidosDetailExpiresAt')} value={localDateTime(order.expiresAt)} /> : null}
          {!deposit ? <DetailRow label={t('transactionFeeShort')} value={`${fmtMoney(order.feeAmount)} (${withdrawFeePercent(order)})`} /> : null}
          {!deposit ? <DetailRow label={t('transactionNetShort')} value={fmtMoney(order.payoutAmount)} /> : null}
        </details>
      </section>
      {bucket !== 'completed' ? (
        <button type="button" className="unpaid-link history-support-link" onClick={() => onSupport(order)}>{t('pedidosDetailContactCta')}</button>
      ) : null}
    </section>
  );
}
